/*
 * @-mentions shared by every composer (agent chat, form comments): the
 * plain-text tokens the server parses (`@Alice Smith <user:uid>`), the
 * candidates a picker offers, and the composer model that shows tokens as
 * pills. The Case IDE adds files and workspace items as candidates.
 */
import type { FsNode } from "@/features/caseFiles/types";
import { stripEntityRefs } from "@/features/agents/refs/entityRefs";
import { ASSISTANT_PERSONA, caseyName } from "@/features/agents/types";
import type { Artifact, ArtifactKind } from "@/features/caseIde/types";
import type { WorkspaceSection } from "@/features/caseIde/workspace/workspaceModel";

export type TeamMember = {
  uid: string;
  label: string;
  email: string;
  avatarUrl?: string | null;
};

export const AGENT_MENTION_ID = "agent";
export const AGENT_LABEL = ASSISTANT_PERSONA;

/**
 * A specific agent named in a stored message (`@Casey · O-1A
 * <agent:case:ind9>`). Without one, `@Casey <agent:agent>` names the thread's
 * own agent. Older messages carry the pre-Casey titles (`@Agent`, `@Case
 * Assistant · O-1A`); they display as Casey.
 */
export type MentionAgentScope = { kind: string; id: string };

/** Who or what an `@` can point at. */
export type MentionTarget =
  | { kind: "artifact"; artifact: Pick<Artifact, "kind" | "id" | "title"> }
  | { kind: "user"; uid: string; label: string }
  | { kind: "agent"; scope?: MentionAgentScope | null; label?: string | null };

type CandidateBase = {
  /** Secondary text: exhibit label, folder path, draft type, email… */
  hint: string | null;
  /** Lower-cased haystack used for filtering. */
  search: string;
};

export type MentionCandidate = CandidateBase &
  (
    | { kind: "artifact"; artifact: Artifact }
    | {
        kind: "user";
        uid: string;
        label: string;
        email: string;
        avatarUrl?: string | null;
      }
    | {
        kind: "agent";
        scope?: MentionAgentScope | null;
        label?: string | null;
      }
  );

const KIND_ORDER: Record<ArtifactKind, number> = {
  file: 0,
  card: 1,
  draft: 2,
  packet: 3,
  form: 4,
  settings: 5,
  plan: 6,
  key_facts: 7,
  time: 8,
  company: 9,
  workflows: 10
};

/** Up to two initials for an avatar fallback. */
export const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "?";

const agentRef = (scope?: MentionAgentScope | null): string =>
  scope ? `agent:${scope.kind}:${scope.id}` : `agent:${AGENT_MENTION_ID}`;

export const candidateKey = (c: MentionCandidate): string =>
  c.kind === "artifact"
    ? `${c.artifact.kind}:${c.artifact.id}`
    : c.kind === "user"
    ? `user:${c.uid}`
    : agentRef(c.scope);

export const candidateLabel = (c: MentionCandidate): string =>
  c.kind === "artifact"
    ? c.artifact.title
    : c.kind === "user"
    ? c.label
    : c.label || AGENT_LABEL;

/** The firm's members as candidates, never yourself. */
export const peopleCandidates = (
  teamMembers: TeamMember[],
  me: string | null = null
): MentionCandidate[] =>
  teamMembers
    .filter((m) => m.uid && m.uid !== me)
    .map((m) => ({
      kind: "user",
      uid: m.uid,
      label: m.label,
      email: m.email,
      avatarUrl: m.avatarUrl ?? null,
      hint: m.email || null,
      search: `${m.label} ${m.email}`.toLowerCase()
    }));

/**
 * Everything that can be mentioned: teammates first (never yourself), then the
 * agent, then files and workspace items.
 */
export const mentionCandidates = (
  nodes: FsNode[],
  labels: Record<string, string | null>,
  sections: WorkspaceSection[],
  teamMembers: TeamMember[] = [],
  me: string | null = null,
  options: { agentHint?: string } = {}
): MentionCandidate[] => {
  const people = peopleCandidates(teamMembers, me);
  const agents: MentionCandidate[] = [
    {
      kind: "agent",
      hint: options.agentHint ?? `Ask ${ASSISTANT_PERSONA}`,
      search: `${ASSISTANT_PERSONA} agent ${
        options.agentHint ?? ""
      } case agent ai assistant`.toLowerCase()
    }
  ];
  const files: MentionCandidate[] = nodes
    .filter((n) => !n.is_deleted)
    .map((n) => {
      const label = labels[n.id] ?? null;
      const hint =
        n.kind === "folder" ? "Folder" : label ? `Exhibit ${label}` : null;
      return {
        kind: "artifact",
        artifact: { kind: "file", id: n.id, title: n.name },
        hint,
        search: `${n.name} ${hint ?? ""} ${n.path_cache}`.toLowerCase()
      };
    });
  const items: MentionCandidate[] = sections.flatMap((s) =>
    s.items.map(
      (i): MentionCandidate => ({
        kind: "artifact",
        artifact: i.artifact,
        hint: i.detail ?? s.title,
        search: `${i.artifact.title} ${i.detail ?? ""} ${s.title}`.toLowerCase()
      })
    )
  );
  const artifacts = [...files, ...items].sort((a, b) => {
    if (a.kind !== "artifact" || b.kind !== "artifact") return 0;
    return (
      KIND_ORDER[a.artifact.kind] - KIND_ORDER[b.artifact.kind] ||
      a.artifact.title.localeCompare(b.artifact.title)
    );
  });
  return [...people, ...agents, ...artifacts];
};

export const filterMentions = (
  candidates: MentionCandidate[],
  query: string,
  limit = 8
): MentionCandidate[] => {
  const q = query.trim().toLowerCase();
  if (!q) return candidates.slice(0, limit);
  const terms = q.split(/\s+/);
  return candidates
    .filter((c) => terms.every((t) => c.search.includes(t)))
    .slice(0, limit);
};

/** Group label shown above a run of candidates in the picker. */
export const candidateGroup = (
  c: MentionCandidate
): "People" | "Agent" | "Agents" | "Files" =>
  c.kind === "user"
    ? "People"
    : c.kind === "agent"
    ? c.scope
      ? "Agents"
      : "Agent"
    : "Files";

const squash = (s: string) => s.replace(/\s+/g, " ").trim();

type ArtifactLike = Pick<Artifact, "kind" | "id" | "title">;

const isTarget = (x: MentionTarget | ArtifactLike): x is MentionTarget =>
  x.kind === "artifact" || x.kind === "user" || x.kind === "agent";

const asTarget = (t: MentionTarget | ArtifactLike): MentionTarget =>
  isTarget(t) ? t : { kind: "artifact", artifact: t };

/** What the user sees for a mention: "Casey", "Alice Smith", "Passport". */
export const mentionLabel = (t: MentionTarget | ArtifactLike): string => {
  const target = asTarget(t);
  return target.kind === "agent"
    ? caseyName(squash(target.label || "")) || AGENT_LABEL
    : target.kind === "user"
    ? squash(target.label)
    : squash(target.artifact.title);
};

/** The `kind:id` the tools and server resolve without a lookup. */
const mentionRef = (target: MentionTarget): string =>
  target.kind === "agent"
    ? agentRef(target.scope)
    : target.kind === "user"
    ? `user:${target.uid}`
    : `${target.artifact.kind}:${target.artifact.id}`;

/**
 * The plain-text token sent to the server. The model reads it as-is; the
 * `<kind:id>` tail is what the tools (or the server, for people) need to
 * resolve it without a lookup: `@Passport <file:n1>`, `@Alice Smith <user:uid>`,
 * `@Casey <agent:agent>`, `@Casey · O-1A <agent:case:ind9>`.
 */
export const mentionToken = (t: MentionTarget | ArtifactLike): string => {
  const target = asTarget(t);
  return `@${mentionLabel(target)} <${mentionRef(target)}>`;
};

export const targetOf = (c: MentionCandidate): MentionTarget =>
  c.kind === "artifact"
    ? { kind: "artifact", artifact: c.artifact }
    : c.kind === "user"
    ? { kind: "user", uid: c.uid, label: c.label }
    : { kind: "agent", scope: c.scope ?? null, label: c.label ?? null };

export type ParsedMention =
  | { kind: "user"; id: string; label: string }
  | { kind: "agent"; id: "agent"; label: string };

/** A specific agent named in the text. */
export type ParsedAgentMention = {
  scopeKind: string;
  scopeId: string;
  label: string;
};

export type ParsedMentions = {
  users: Array<{ kind: "user"; id: string; label: string }>;
  /** Any agent named: the bare `@Agent` or a scoped one. */
  agentMentioned: boolean;
  /** Scoped agents named, in order. */
  agents: ParsedAgentMention[];
  /**
   * Slack rule, mirrored on the server: an agent answers when one is named or
   * when nobody is. People-only mentions are team chat.
   */
  addressedToAgent: boolean;
};

const TOKEN = /@([^<>\n@]*?)\s*<([a-z_]+):([^<>\s]+)>/g;

export const parseMentions = (text: string): ParsedMentions => {
  const users: ParsedMentions["users"] = [];
  const agents: ParsedAgentMention[] = [];
  const seen = new Set<string>();
  let agentMentioned = false;
  for (const m of text.matchAll(TOKEN)) {
    const [, rawLabel, kind, id] = m;
    if (kind === "user") {
      if (!seen.has(id)) {
        seen.add(id);
        users.push({ kind: "user", id, label: squash(rawLabel) || id });
      }
    } else if (kind === "agent") {
      agentMentioned = true;
      const colon = id.indexOf(":");
      if (colon > 0 && !seen.has(`agent:${id}`)) {
        seen.add(`agent:${id}`);
        agents.push({
          scopeKind: id.slice(0, colon),
          scopeId: id.slice(colon + 1),
          label: caseyName(squash(rawLabel)) || AGENT_LABEL
        });
      }
    }
  }
  return {
    users,
    agentMentioned,
    agents,
    addressedToAgent: agentMentioned || users.length === 0
  };
};

export type TextSegment =
  | { type: "text"; text: string }
  | {
      type: "mention";
      kind: "user" | "agent" | "artifact";
      id: string;
      label: string;
    };

/** Split message text into plain runs and mention chips for rendering. */
export const segmentMentions = (text: string): TextSegment[] => {
  const out: TextSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    const start = m.index ?? 0;
    if (start > last) out.push({ type: "text", text: text.slice(last, start) });
    const [, rawLabel, kind, id] = m;
    const label = squash(rawLabel);
    out.push({
      type: "mention",
      kind: kind === "user" ? "user" : kind === "agent" ? "agent" : "artifact",
      id,
      label: (kind === "agent" ? caseyName(label) : label) || id
    });
    last = start + m[0].length;
  }
  if (last < text.length) out.push({ type: "text", text: text.slice(last) });
  return out;
};

/**
 * `hi @Alice Smith <user:uid>` -> `hi @Alice Smith`, and `[Doc](/path "…")` ->
 * `Doc`, for titles and previews.
 */
export const plainMentions = (text: string): string =>
  stripEntityRefs(
    text.replace(TOKEN, (_, rawLabel: string, kind: string, id: string) => {
      const label = squash(rawLabel);
      return `@${(kind === "agent" ? caseyName(label) : label) || id}`;
    })
  );

export type ActiveMention = { start: number; query: string };

/**
 * If the caret sits inside an `@query` being typed, return where it starts and
 * what has been typed so far. An `@` preceded by a word character (email-like)
 * or a query containing a `<` (an already-inserted token) is not a mention.
 */
export const activeMentionAt = (
  text: string,
  caret: number,
  /** Pills already in the text: their leading `@` never starts a new query. */
  ranges: readonly Pick<MentionRange, "start">[] = []
): ActiveMention | null => {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && /\w/.test(before[at - 1])) return null;
  if (ranges.some((r) => r.start === at)) return null;
  const query = before.slice(at + 1);
  if (/[\n<>]/.test(query) || query.length > 60) return null;
  return { start: at, query };
};

// ---------------------------------------------------------------------------
// Composer model: what the user sees vs. what is sent
// ---------------------------------------------------------------------------

/** A pill in the composer's display text: the run `@Label` at [start, end). */
export type MentionRange = {
  start: number;
  end: number;
  target: MentionTarget;
};

/**
 * The composer's text as displayed (`hi @Alice Smith `) plus where the pills
 * are. `toWire` turns it into the token form the server expects.
 */
export type ComposerModel = { display: string; mentions: MentionRange[] };

export const EMPTY_MODEL: ComposerModel = { display: "", mentions: [] };

const byStart = (a: MentionRange, b: MentionRange) => a.start - b.start;

/**
 * The reverse of `toWire` for people, to edit stored text: each
 * `@Name <user:uid>` becomes a pill; other tokens stay as written.
 */
export const fromWire = (text: string): ComposerModel => {
  let display = "";
  let last = 0;
  const mentions: MentionRange[] = [];
  const users = [...text.matchAll(TOKEN)].filter((m) => m[2] === "user");
  for (const m of users) {
    const [whole, rawLabel, , id] = m;
    const start = m.index ?? 0;
    display += text.slice(last, start);
    const label = squash(rawLabel) || id;
    const pill = `@${label}`;
    mentions.push({
      start: display.length,
      end: display.length + pill.length,
      target: { kind: "user", uid: id, label }
    });
    display += pill;
    last = start + whole.length;
  }
  return { display: display + text.slice(last), mentions };
};

/** Display text with each pill expanded to its `@Label <kind:id>` token. */
export const toWire = (model: ComposerModel): string => {
  let out = "";
  let last = 0;
  for (const m of [...model.mentions].sort(byStart)) {
    out += `${model.display.slice(last, m.end)} <${mentionRef(m.target)}>`;
    last = m.end;
  }
  return out + model.display.slice(last);
};

export type ModelSegment =
  | { type: "text"; text: string }
  | { type: "mention"; text: string; target: MentionTarget };

/** The display text split into plain runs and pills, for the highlight layer. */
export const modelSegments = (model: ComposerModel): ModelSegment[] => {
  const out: ModelSegment[] = [];
  let last = 0;
  for (const m of [...model.mentions].sort(byStart)) {
    if (m.start > last)
      out.push({ type: "text", text: model.display.slice(last, m.start) });
    out.push({
      type: "mention",
      text: model.display.slice(m.start, m.end),
      target: m.target
    });
    last = m.end;
  }
  if (last < model.display.length)
    out.push({ type: "text", text: model.display.slice(last) });
  return out;
};

/**
 * Replace display[start, end) with a pill (and a trailing space). Pills that
 * overlapped the replaced span are dropped; later ones shift.
 */
const splice = (
  model: ComposerModel,
  start: number,
  end: number,
  target: MentionTarget | ArtifactLike
): { model: ComposerModel; caret: number } => {
  const label = `@${mentionLabel(target)}`;
  const inserted = `${label} `;
  const delta = inserted.length - (end - start);
  const mentions = model.mentions
    .filter((m) => m.end <= start || m.start >= end)
    .map((m) =>
      m.start >= end ? { ...m, start: m.start + delta, end: m.end + delta } : m
    );
  mentions.push({ start, end: start + label.length, target: asTarget(target) });
  return {
    model: {
      display:
        model.display.slice(0, start) + inserted + model.display.slice(end),
      mentions: mentions.sort(byStart)
    },
    caret: start + inserted.length
  };
};

/** Turn the `@query` being typed (from `mention.start` to the caret) into a pill. */
export const insertMentionAt = (
  model: ComposerModel,
  mention: ActiveMention,
  caret: number,
  target: MentionTarget | ArtifactLike
): { model: ComposerModel; caret: number } =>
  splice(model, mention.start, caret, target);

/** Add a pill at the end (a tree pushed a mention in). */
export const appendMention = (
  model: ComposerModel,
  target: MentionTarget | ArtifactLike
): { model: ComposerModel; caret: number } => {
  const sep = model.display && !/\s$/.test(model.display) ? " " : "";
  const start = model.display.length + sep.length;
  return splice(
    { ...model, display: model.display + sep },
    start,
    start,
    target
  );
};

/**
 * Bring the pills in line with what the textarea now holds after a free-form
 * edit. The edit is found as the span between the common prefix and suffix
 * of the two texts; pills before it stay, pills after it shift, and a pill
 * the edit touches is either deleted whole (a pure deletion, like Backspace
 * at its end, so pills behave atomically) or unwrapped into plain text
 * (something was typed into it). `caret` is set when the display had to be
 * changed beyond what the textarea did, so the caller can re-place it.
 */
export const reconcileDisplay = (
  prev: ComposerModel,
  next: string,
  options: { atomicDelete?: boolean } = {}
): { model: ComposerModel; caret?: number } => {
  const atomicDelete = options.atomicDelete ?? true;
  if (prev.display === next) return { model: prev };
  if (!next) return { model: EMPTY_MODEL };
  if (!prev.mentions.length) return { model: { display: next, mentions: [] } };

  const a = prev.display;
  const b = next;
  const max = Math.min(a.length, b.length);
  let p = 0;
  while (p < max && a[p] === b[p]) p += 1;
  let s = 0;
  while (s < max - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s += 1;
  const oldEnd = a.length - s;
  const newEnd = b.length - s;
  const insertedText = b.slice(p, newEnd);
  const pureDeletion = insertedText.length === 0;

  // Pills the edit ran into: delete whole (deletion) or unwrap (typing).
  let cutStart = p;
  let cutEnd = oldEnd;
  const kept: MentionRange[] = [];
  for (const m of prev.mentions) {
    if (m.end <= p || m.start >= oldEnd) {
      kept.push(m);
    } else if (pureDeletion && atomicDelete) {
      cutStart = Math.min(cutStart, m.start);
      cutEnd = Math.max(cutEnd, m.end);
    }
  }
  const display = a.slice(0, cutStart) + insertedText + a.slice(cutEnd);
  const delta = display.length - a.length;
  const mentions = kept
    .filter((m) => m.end <= cutStart || m.start >= cutEnd)
    .map((m) =>
      m.start >= cutEnd
        ? { ...m, start: m.start + delta, end: m.end + delta }
        : m
    )
    .sort(byStart);
  return display === b
    ? { model: { display, mentions } }
    : { model: { display, mentions }, caret: cutStart + insertedText.length };
};

/** Replace the active `@query` with the token and return the new text and caret. */
export const applyMention = (
  text: string,
  mention: ActiveMention,
  caret: number,
  target: MentionTarget | ArtifactLike
): { text: string; caret: number } => {
  const token = `${mentionToken(target)} `;
  const next = text.slice(0, mention.start) + token + text.slice(caret);
  return { text: next, caret: mention.start + token.length };
};
